import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import PageHeader from 'components/atoms/PageHeader'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import {
  getBpPrices,
  getBpPricesStatus
} from 'features/bpList/ducks/selectors'
import {
  clearBpPricesRoutine,
  getBpsPricesRoutine
} from 'features/bpList/ducks/actions'
import { addToCart, getCart } from 'utils/localStorage'
import ItemsTable from 'features/cart/components/ItemsTable'
import { cartEmitter } from 'utils/emitters'
import EmptyState from 'features/cart/components/EmptyState'
import CartSummary from 'features/cart/components/CartSummary'
import { pathOr } from 'ramda'
import { isNotNilOrEmpty } from 'utils/ramda'
import {
  selectBasketAvailabilityStatus,
  selectCartAvailableBps
} from 'features/cart/ducks/selectors'
import { checkBasketAvailabilityRoutine, clearBasketAvailabilityRoutine } from 'features/cart/ducks/actions'
import { SkeletonRows } from 'components/Skeleton'

// Renewal emails link straight to `/cart?add=<bpId>` (BP-311). Only a UUID is
// honoured, and the parameter is dropped from the URL once consumed so a
// refresh cannot re-add the item.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const Cart = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const addParam = searchParams.get('add')
  const prices = useSelector(getBpPrices)
  const pricesStatus = useSelector(getBpPricesStatus)
  const basketAvailabilityStatus = useSelector(selectBasketAvailabilityStatus)
  const [localCartIds, setLocalCartIds] = useState(() => getCart())
  const [isRefreshing, setIsRefreshing] = useState(() =>
    isNotNilOrEmpty(getCart())
  )
  const [cartData, setCartData] = useState({
    prices: {},
    bps: []
  })
  const availableBps = useSelector(selectCartAvailableBps)

  useEffect(() => {
    if (pricesStatus === 'success' && prices) {
      setCartData({
        prices: prices.summaryPrices,
        bps: prices.buildingPassportsList
      })
      setIsRefreshing(false)
    }
  }, [prices, pricesStatus])

  useEffect(() => {
    if (
      basketAvailabilityStatus === 'failure' ||
      pricesStatus === 'failure'
    ) {
      setIsRefreshing(false)
    }
  }, [basketAvailabilityStatus, pricesStatus])

  const prepareValues = () => {
    const cartBps = getCart()
    setLocalCartIds(cartBps)

    if (isNotNilOrEmpty(cartBps)) {
      setIsRefreshing(true)
      dispatch(clearBpPricesRoutine())
      dispatch(checkBasketAvailabilityRoutine({
        buildingPassportIds: cartBps
      }))
    } else {
      setIsRefreshing(false)
      dispatch(clearBpPricesRoutine())
      dispatch(clearBasketAvailabilityRoutine())
      setCartData({
        prices: {},
        bps: []
      })
    }
  }

  useEffect(() => {
    if (basketAvailabilityStatus !== 'success') return

    if (isNotNilOrEmpty(availableBps)) {
      setIsRefreshing(true)
      dispatch(getBpsPricesRoutine({
        buildingPassportsIds: availableBps
      }))
    } else {
      setIsRefreshing(false)
      setCartData({
        prices: {},
        bps: []
      })
    }
  }, [availableBps, basketAvailabilityStatus, dispatch])

  useEffect(() => {
    if (!addParam) return
    if (UUID_PATTERN.test(addParam) && !getCart().includes(addParam)) {
      addToCart(addParam)
    }
    navigate({ search: '' }, { replace: true })
  }, [addParam, navigate])

  useEffect(() => {
    prepareValues()
    cartEmitter.on('cart-change', prepareValues)

    return () => {
      cartEmitter.off('cart-change', prepareValues)
    }
  }, [])

  const isLoading = isRefreshing ||
    basketAvailabilityStatus === 'loading' ||
    pricesStatus === 'loading'
  const hasLoadingError =
    basketAvailabilityStatus === 'failure' ||
    pricesStatus === 'failure'
  const hasLocalCartItems = isNotNilOrEmpty(localCartIds)
  let cartContent = <EmptyState />

  if (isLoading) {
    cartContent = (
      <CartLoading>
        <SkeletonRows rows={3} widths={['44%', '18%', '18%', '14%']} />
      </CartLoading>
    )
  } else if (hasLoadingError && hasLocalCartItems) {
    cartContent = (
      <LoadError role='alert'>
        <LoadErrorTitle>We couldn&apos;t load your cart.</LoadErrorTitle>
        <RetryButton onClick={prepareValues}>Try again</RetryButton>
      </LoadError>
    )
  } else if (pathOr([], ['bps'], cartData).length > 0) {
    cartContent = <ItemsTable bps={cartData.bps} />
  }

  return (
    <PageWrapper>
      <ContentWrapper>
        <PageHeader>Your cart</PageHeader>
        {cartContent}
      </ContentWrapper>
      <DetailsWrapper>
        {isLoading
          ? (
            <SummaryLoading>
              <SkeletonRows rows={4} widths={['60%', '30%']} />
            </SummaryLoading>
            )
          : <CartSummary prices={cartData.prices} items={cartData.bps}/>}
      </DetailsWrapper>
    </PageWrapper>
  )
}

export default Cart

const PageWrapper = styled.div`
  display: flex;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`

const ContentWrapper = styled.div`
  width: 100%;
  padding-right: 320px;

  @media (max-width: 1024px) {
    padding-right: 0;
    margin-bottom: 20px;
  }
`

const CartLoading = styled.div`
  margin: 50px auto 0;
  max-width: 900px;
  padding: 30px;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.white};
`

const LoadError = styled.div`
  margin: 50px auto 0;
  max-width: 500px;
  padding: 30px;
  text-align: center;
  background: ${({ theme }) => theme.colors.white};
`

const LoadErrorTitle = styled.div`
  margin-bottom: 18px;
  font-size: 20px;
  color: ${({ theme }) => theme.colors.haiti};
`

const RetryButton = styled.button`
  border: 0;
  border-radius: 20px;
  padding: 10px 24px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.colors.haiti};
`

const SummaryLoading = styled.div`
  padding-top: 35px;
`

const DetailsWrapper = styled.div`
  width: 300px;
  position: fixed;
  top: ${({ theme }) => theme.dimensions.topBarHeight};
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.lighterGrey};
  padding: 25px 20px;
  overflow-y: auto;

  @media (max-width: 1024px) {
    position: static;
    width: 100%;
    height: auto;
    overflow-y: visible;
    padding: 20px;
    box-sizing: border-box;
  }
`
